import React, { useState, useMemo, useEffect } from 'react';
import { ResponsiveBar } from '@nivo/bar';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { formatNumberWithSpaces } from 'utils/index';
import './CultureStackedChart.style.scss';
import { sortByCAPCategory } from '../utils/cultureSort';
import { useTranslation } from 'react-i18next';

const CultureStackedChart = ({ id, pieChartData, title, forcedMode, isOpen, onToggle }) => {
  const { t } = useTranslation();
  const [dataMode, setDataMode] = useState('grossMargin');

  const DATA_MODES = [
    { key: 'grossMargin', label: t('simulation.result.cultures.grossMarginEur') },
    { key: 'hectares', label: t('common.surfaceHa') },
  ];

  useEffect(() => {
    if (forcedMode) setDataMode(forcedMode);
  }, [forcedMode]);

  const { barData, culturesKeys, colorMap } = useMemo(() => {
    if (!pieChartData?.length) return { barData: [], culturesKeys: [], colorMap: {} };

    const keys = [];
    pieChartData.forEach((yearData) => {
      yearData.data.map(({ label, capCategory }) => {
        if (!keys.find((key) => key.label === label)) keys.push({ label, capCategory });
      });
    });

    const colorMap = {};
    pieChartData.forEach((yearData) => {
      yearData.data.forEach(({ label, color }) => {
        if (!colorMap[label]) colorMap[label] = color;
      });
    });

    const barData = pieChartData.map((yearData) => {
      const entry = { campaign: `${yearData.year}-${yearData.year + 1}` };
      keys.forEach((key) => {
        const found = yearData.data.find((c) => c.label === key.label);
        entry[key.label] = found ? Number(found[dataMode].toFixed(2)) : 0;
      });
      return entry;
    });

    return {
      barData,
      culturesKeys: keys.sort(sortByCAPCategory).map((key) => key.label),
      colorMap,
    };
  }, [pieChartData, dataMode]);

  if (!pieChartData?.length) return null;

  const isGrossMargin = dataMode === 'grossMargin';

  const formatValue = (value) => {
    if (isGrossMargin) return `${formatNumberWithSpaces(Math.round(value))} €`;
    return `${formatNumberWithSpaces(Number(value).toFixed(1))} ha`;
  };

  return (
    <section id={id} className="section">
      <div className="section-header section-header clickable" onClick={onToggle}>
        <h2 className="section-title">{title}</h2>
        {isOpen ? (
          <div className="stream-chart__toggle" onClick={(e) => e.stopPropagation()}>
            {DATA_MODES.map((mode) => (
              <button
                key={mode.key}
                className={`stream-chart__toggle-btn ${dataMode === mode.key ? 'stream-chart__toggle-btn--active' : ''}`}
                onClick={() => setDataMode(mode.key)}
              >
                {mode.label}
              </button>
            ))}
          </div>
        ) : null}
        {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </div>

      {isOpen && (
        <>
          <div className="stream-chart__container">
            <ResponsiveBar
              data={barData}
              keys={[...culturesKeys].reverse()}
              indexBy="campaign"
              groupMode="stacked"
              margin={{ top: 20, right: 20, bottom: 50, left: 80 }}
              padding={0.3}
              colors={(bar) => colorMap[bar.id] || '#999'}
              borderWidth={0}
              enableLabel={false}
              enableGridY={true}
              enableGridX={false}
              axisTop={null}
              axisRight={null}
              valueScale={{ type: 'linear', min: 'auto', clamp: true }}
              axisBottom={{ tickSize: 5, tickPadding: 5, tickRotation: 0 }}
              axisLeft={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                format: (value) =>
                  isGrossMargin
                    ? `${formatNumberWithSpaces(Math.round(value))} €`
                    : `${Math.round(value)} ha`,
              }}
              tooltip={({ id, indexValue }) => (
                <div className="stream-chart__tooltip">
                  <strong className="stream-chart__tooltip-title">{indexValue}</strong>
                  <div className="stream-chart__tooltip-list">
                    {culturesKeys
                      .filter((key) => {
                        const entry = barData.find((d) => d.campaign === indexValue);
                        return entry?.[key] !== 0;
                      })
                      .map((key) => {
                        const entry = barData.find((d) => d.campaign === indexValue);
                        return (
                          <div
                            key={key}
                            className="stream-chart__tooltip-row"
                            style={{ opacity: key === id ? 1 : 0.5 }}
                          >
                            <span
                              className="stream-chart__tooltip-color"
                              style={{ backgroundColor: colorMap[key] }}
                            />
                            <span className="stream-chart__tooltip-label">{key}</span>
                            <span className="stream-chart__tooltip-value">
                              {formatValue(entry?.[key] || 0)}
                            </span>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
              legends={[]}
            />
          </div>
          <div className="stream-chart__legend">
            {culturesKeys
              .filter((key) => barData.some((d) => d[key] !== 0))
              .map((key) => (
                <div key={key} className="stream-chart__legend-item">
                  <span
                    className="stream-chart__legend-color"
                    style={{ backgroundColor: colorMap[key] }}
                  />
                  <span className="stream-chart__legend-label">{key}</span>
                </div>
              ))}
          </div>
        </>
      )}
    </section>
  );
};

export default CultureStackedChart;
